// Collection completion DISPLAY helpers shared by web and mobile.
//
// Display only. Whether a collection is complete, how many entries unlock it
// and how many remain are computed by the backend
// (apps/backend/src/services/collectionCompletionService.js) and arrive on every
// collection as completionType / totalEntries / verifiedEntries /
// requiredForCompletion / remainingRequired / isComplete. Older payloads without
// them fall back to progress / total / remaining (all-required semantics).

export const COMPLETION_TYPE = Object.freeze({
    ALL_REQUIRED: 'ALL_REQUIRED',
    MINIMUM_COUNT: 'MINIMUM_COUNT',
});

/** Normalized numbers for rendering. */
export function completionNumbers(c = {}) {
    const total = c.totalEntries ?? c.total ?? 0;
    const verified = c.verifiedEntries ?? c.progress ?? 0;
    const required = c.requiredForCompletion ?? total;
    return {
        isMinimum: c.completionType === COMPLETION_TYPE.MINIMUM_COUNT,
        total,
        verified,
        required,
        // Credit toward unlocking (never above the threshold).
        towardRequired: Math.min(verified, required),
        remaining: c.remainingRequired ?? c.remaining ?? Math.max(required - verified, 0),
        isComplete: c.isComplete ?? (required > 0 && verified >= required),
    };
}

/** "Any 5 of 8" for MINIMUM_COUNT collections, else null (all-required reads as before). */
export function requirementLabel(c) {
    const n = completionNumbers(c);
    return n.isMinimum ? `Any ${n.required} of ${n.total}` : null;
}

/**
 * Primary progress line:
 *   ALL_REQUIRED  "3 / 4 verified"           (unchanged)
 *   MINIMUM_COUNT "3 of 5 required" / "5 of 5 required ✓"
 */
export function progressLabel(c) {
    const n = completionNumbers(c);
    if (!n.isMinimum) return `${n.verified} / ${n.total} verified`;
    return `${n.towardRequired} of ${n.required} required${n.isComplete ? ' ✓' : ''}`;
}

/** Secondary line for MINIMUM_COUNT: "3 of 8 titles verified"; null otherwise. */
export function secondaryProgressLabel(c) {
    const n = completionNumbers(c);
    if (!n.isMinimum) return null;
    return `${n.verified} of ${n.total} title${n.total === 1 ? '' : 's'} verified`;
}

/** Progress-bar / coin-reveal fraction toward unlocking (0..1). */
export function unlockFraction(c) {
    const n = completionNumbers(c);
    return n.required > 0 ? n.towardRequired / n.required : 0;
}

/**
 * Requirement sentence for the detail page:
 *   "Verify all 4 films to unlock X."
 *   "Verify any 5 of 8 titles to unlock X."
 */
export function requirementSentence(c, { titleWord = 'titles', rewardName = 'the reward coin' } = {}) {
    const n = completionNumbers(c);
    return n.isMinimum
        ? `Verify any ${n.required} of ${n.total} ${titleWord} to unlock ${rewardName}.`
        : `Verify all ${n.total} ${titleWord} to unlock ${rewardName}.`;
}
